import { useEffect, useMemo } from 'react'

import { useCompareExtGapMutation, useTimelineExtGapMutation } from '@/common/api/modules/article'
import { CannibalizationAnalysis, ExtGapType } from '@/common/model/media/articleCreator'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import DualAxisLineChart from '@/components/view/atoms/chart/DualAxisLineChart'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'

import AnalysisActionList from '@/components/container/modules/media/action/AnalysisActionList'

interface Props {
  siteId: number
  historyId: number
  analysis: CannibalizationAnalysis
}

const severityBadgeType = (severity: string | undefined): BadgeType => {
  switch (severity) {
    case 'high':
      return BadgeType.CRITICAL
    case 'medium':
      return BadgeType.CAUTION
    default:
      return BadgeType.SUCCESS
  }
}

const actionBadgeType = (action: string | undefined): BadgeType => {
  switch (action) {
    case '統合':
      return BadgeType.CRITICAL
    case '差別化':
      return BadgeType.CAUTION
    default:
      return BadgeType.SUCCESS
  }
}

const pairKey = (a: string, b: string) => [a, b].sort().join('::')

// 統計サマリー（分析ページ数・重複ペア数・深刻な重複・平均重複度）
const StatsRow = ({
  summary,
  avgOverlapPercent
}: {
  summary: CannibalizationAnalysis['summary']
  avgOverlapPercent: number
}) => {
  const s = summary ?? {}
  const tiles = [
    { label: '分析ページ数', value: s.total_pages ?? 0 },
    { label: '重複ペア数', value: s.issue_count ?? 0, color: TextColor.CAUTION },
    { label: '深刻な重複', value: s.high_severity_count ?? 0, color: TextColor.CRITICAL },
    { label: '平均重複度', value: avgOverlapPercent, suffix: '%' }
  ]
  return (
    <Area bottomMargin={16}>
      <SummaryItemList shadow>
        {tiles.map((t, index) => (
          <SummaryItem key={index}>
            <Text size={12} weight='bold'>
              {t.label}
            </Text>
            <ItemAlign gapX={4} align='baseline'>
              <Text size={24} weight='bold' color={t.color}>
                {t.value}
              </Text>
              {t.suffix && (
                <Text size={14} color={TextColor.SECONDARY}>
                  {t.suffix}
                </Text>
              )}
            </ItemAlign>
          </SummaryItem>
        ))}
      </SummaryItemList>
    </Area>
  )
}

// 重複ペア一覧（深刻度・重複度・前回比較の新規判定・推奨アクション）
const PairsSection = ({
  pairs,
  newPairKeys
}: {
  pairs: NonNullable<CannibalizationAnalysis['cannibalization']>
  newPairKeys: Set<string>
}) => {
  if (pairs.length === 0) return null
  return (
    <Area bottomMargin={16}>
      <Heading>重複ペア ({pairs.length}件)</Heading>
      <Line />
      <AreaContent>
        <div className='flex flex-col gap-3'>
          {pairs.map((c, i) => (
            <PriorityBar
              key={i}
              priority={c.severity ?? 'low'}
              title={undefined}
              contentText={
                <div className='flex flex-col gap-2.5'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <Badge size={10} badgeType={severityBadgeType(c.severity)}>
                      重複度 {Math.round((c.overlap_score || 0) * 100)}%
                    </Badge>
                    {newPairKeys.has(pairKey(c.page_a_title, c.page_b_title)) && (
                      <Badge size={10} badgeType={BadgeType.CRITICAL}>
                        新規
                      </Badge>
                    )}
                  </div>
                  <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-3'>
                    <InsideArea color={BgColor.WHITE} border>
                      <div className='flex flex-col gap-1'>
                        <Text size={10} color={TextColor.LINK}>
                          ページA
                        </Text>
                        <Text size={12} weight='bold'>
                          {c.page_a_title}
                        </Text>
                      </div>
                    </InsideArea>
                    <Text size={20} color={TextColor.SECONDARY}>
                      ⇔
                    </Text>
                    <InsideArea color={BgColor.WHITE} border>
                      <div className='flex flex-col gap-1'>
                        <Text size={10} color={TextColor.PURPLE}>
                          ページB
                        </Text>
                        <Text size={12} weight='bold'>
                          {c.page_b_title}
                        </Text>
                      </div>
                    </InsideArea>
                  </div>
                  {c.overlapping_keywords && c.overlapping_keywords.length > 0 && (
                    <div className='flex flex-col gap-1'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        重複キーワード
                      </Text>
                      <div className='flex flex-wrap gap-1'>
                        {c.overlapping_keywords.map((kw, ki) => (
                          <Badge key={ki} size={10} badgeType={BadgeType.CAUTION}>
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              }
              subContent={
                c.recommended_action && (
                  <ItemAlign gapX={4} align='center' direction='horizon' nowrap>
                    <Icon type={IconType.ARROW_RIGHT} />
                    <div className='w-90'>
                      <AiInsideArea border>
                        <div className='flex flex-col gap-1.5 p-1'>
                          <Badge size={10} badgeType={actionBadgeType(c.recommended_action)}>
                            推奨: {c.recommended_action}
                          </Badge>
                          {c.action_detail && (
                            <Text size={12} color={TextColor.AI} lineHeight={150}>
                              {c.action_detail}
                            </Text>
                          )}
                        </div>
                      </AiInsideArea>
                    </div>
                  </ItemAlign>
                )
              }
            />
          ))}
        </div>
      </AreaContent>
    </Area>
  )
}

// 時系列分析（過去実行の推移）
const TimelineSection = ({ siteId }: { siteId: number }) => {
  const { trigger: fetchTimeline, data: timeline } = useTimelineExtGapMutation(
    ExtGapType.CANNIBALIZATION
  )

  useEffect(() => {
    fetchTimeline({ site_id: siteId }).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  if (!timeline) return null

  return (
    <>
      <SummaryHeading subText='分析履歴が2回以上ある場合にAIが対応状況を評価（過去10回分が対象）。優先度付きアクションは①②に集約しているため、ここではトレンドとリスクのみ表示します。'>
        時系列分析（過去実行の推移）
      </SummaryHeading>
      <Area bottomMargin={16}>
        <AreaContent>
          {!timeline.available ? (
            <Text size={12} color={TextColor.SECONDARY}>
              {timeline.message ?? '時系列分析には2回以上の分析履歴が必要です'}
            </Text>
          ) : (
            <>
              <InsideArea color={BgColor.SECONDARY}>
                <div className='h-[260px] w-full'>
                  <DualAxisLineChart
                    labels={(timeline.timeline_data ?? []).map((r) => String(r.date))}
                    countLines={[
                      {
                        title: '重複ペア数',
                        values: (timeline.timeline_data ?? []).map(
                          (r) => Number(r.issue_count) || 0
                        )
                      },
                      {
                        title: '深刻な重複',
                        values: (timeline.timeline_data ?? []).map(
                          (r) => Number(r.high_severity_count) || 0
                        )
                      }
                    ]}
                    scoreLine={{
                      title: '平均重複度',
                      values: (timeline.timeline_data ?? []).map((r) =>
                        Math.round((Number(r.avg_overlap_score) || 0) * 100)
                      )
                    }}
                  />
                </div>
              </InsideArea>
              <SummaryItemList shadow>
                <SummaryItem>
                  <Text size={12} weight='bold'>
                    全体トレンド
                  </Text>
                  <Text size={24} weight='bold' color={TextColor.SUCCESS}>
                    {timeline.ai_evaluation?.period_evaluation?.overall_trend ?? '-'}
                  </Text>
                </SummaryItem>
                <SummaryItem>
                  <Text size={12} weight='bold'>
                    対応評価スコア
                  </Text>
                  <ItemAlign gapX={4} align='baseline'>
                    <Text size={24} weight='bold'>
                      {timeline.ai_evaluation?.period_evaluation?.score ?? '-'}
                    </Text>
                    <Text size={12} color={TextColor.SECONDARY}>
                      /100
                    </Text>
                  </ItemAlign>
                </SummaryItem>
              </SummaryItemList>
              {timeline.ai_evaluation?.period_evaluation?.summary && (
                <AiInsideArea border>
                  <ItemAlign align='start' gapX={4} nowrap>
                    <Icon type={IconType.AI_FILL} />
                    <Text size={14} color={TextColor.AI}>
                      {timeline.ai_evaluation.period_evaluation.summary}
                    </Text>
                  </ItemAlign>
                </AiInsideArea>
              )}
            </>
          )}
        </AreaContent>
      </Area>
    </>
  )
}

// カニバリゼーション検出パネル
const CannibalizationPanel = ({ siteId, historyId, analysis }: Props) => {
  const summary = analysis.summary
  const pairs = useMemo(() => analysis.cannibalization ?? [], [analysis.cannibalization])

  const avgOverlapPercent = useMemo(
    () =>
      pairs.length > 0
        ? Math.round(
            (pairs.reduce((sum, p) => sum + (p.overlap_score || 0), 0) / pairs.length) * 100
          )
        : 0,
    [pairs]
  )

  const { trigger: fetchComparison, data: comparison } = useCompareExtGapMutation(
    ExtGapType.CANNIBALIZATION
  )
  useEffect(() => {
    fetchComparison({ site_id: siteId }).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, historyId])

  const newPairKeys = useMemo(
    () => new Set((comparison?.new_pairs ?? []).map((p) => pairKey(p.page_a, p.page_b))),
    [comparison]
  )

  return (
    <>
      {summary && <StatsRow summary={summary} avgOverlapPercent={avgOverlapPercent} />}
      <AnalysisActionList siteId={siteId} mediaAnalysisId={historyId} />
      {pairs.length > 0 ? (
        <PairsSection pairs={pairs} newPairKeys={newPairKeys} />
      ) : (
        summary && (
          <AiInsideArea border>
            <ItemAlign align='start' gapX={4} nowrap>
              <Icon type={IconType.AI_FILL} />
              <Text size={14} color={TextColor.AI}>
                {summary.recommendation || 'カニバリゼーションは検出されませんでした'}
              </Text>
            </ItemAlign>
          </AiInsideArea>
        )
      )}
      <TimelineSection siteId={siteId} />
    </>
  )
}

export default CannibalizationPanel
